/**
 * GRAPH THEME — Design tokens for graph visualization
 * 
 * Integrates with the core design system (tokens.ts) for shared values.
 * Graph-specific styling lives here to keep it separate but consistent.
 * 
 * When updating the graph look & feel, only change this file —
 * both GraphExplorer and SubgraphModal will get the updates.
 */

import { tokens } from '../../styles/tokens';

// ============================================================
// NODE COLORS — By node type
// ============================================================
export const NODE_COLORS: Record<string, string> = {
    Document: '#1976D2',   // Strong blue
    Chunk: '#388E3C',      // Deep green  
    Entity: '#7B1FA2',     // Deep purple
};

// ============================================================
// HIGHLIGHT COLORS — For selection and connections
// ============================================================
export const HIGHLIGHT_COLORS = {
    selected: tokens.colors.accent,          // Use accent color for consistency
    connected: '#00BFA5',                    // Bright teal for connected nodes
    edge: '#00BFA5',                         // Same teal for highlighted edges
};

/** Theme-aware highlight colors */
export const getHighlightColors = (isDarkMode: boolean) => ({
    ...HIGHLIGHT_COLORS,
    connected: isDarkMode ? '#1DE9B6' : '#00BFA5', // Brighter teal for dark mode
    edge: isDarkMode ? '#1DE9B6' : '#00BFA5',
});

// ============================================================
// BACKGROUND — Uses design system surface colors
// ============================================================
export const GRAPH_BACKGROUNDS = {
    light: tokens.colors.surfaceSubtle,      // Light mode background
    dark: tokens.darkColors.surfaceMuted,    // Dark mode background
};

// Default background (can be overridden per-component)
export const GRAPH_BG = GRAPH_BACKGROUNDS.light;

/** Theme-aware background color */
export const getGraphBg = (isDarkMode: boolean) =>
    isDarkMode ? GRAPH_BACKGROUNDS.dark : GRAPH_BACKGROUNDS.light;

// ============================================================
// CANVAS TEXT STYLING — For node and edge labels
// ============================================================
export const LABEL_STYLES = {
    fontFamily: '-apple-system, system-ui, sans-serif',
    fontSize: {
        node: 12,
        edge: 9,
        nodeMin: 6,
        nodeMax: 14,
        edgeMin: 4,
        edgeMax: 11,
    },
    colors: {
        default: tokens.colors.text,
        secondary: tokens.colors.textSecondary,
        muted: tokens.colors.textMuted,
        onHighlight: '#ffffff',
    },
};

/** Theme-aware label colors */
export const getLabelColors = (isDarkMode: boolean) => {
    const colors = isDarkMode ? tokens.darkColors : tokens.colors;
    return {
        default: colors.text,
        secondary: colors.textSecondary,
        muted: colors.textMuted,
        onHighlight: '#ffffff',
    };
};

// ============================================================
// NODE SIZING — Base sizes and scaling
// ============================================================
export const NODE_SIZING = {
    // Base radius by node type
    baseRadius: {
        Document: 8,
        Chunk: 4,
        Entity: 5,
    },
    // Degree scaling (for hub nodes)
    degreeMultiplier: 4,
    // Hover/selection bonus
    hoverBonus: 3,
    selectedBonus: 4,
    connectedBonus: 2,
};

// ============================================================
// EDGE STYLING
// ============================================================
export const EDGE_STYLES = {
    // Default colors
    colors: {
        fact: 'rgba(123, 31, 162, 0.4)',       // Purple for FACT edges
        default: 'rgba(100, 100, 100, 0.25)',  // Gray for other edges
        highlighted: HIGHLIGHT_COLORS.edge,
    },
    // Width
    width: {
        fact: 2,
        default: 1,
        highlighted: 4,
    },
    // Arrow styling
    arrow: {
        length: 6,
        relPos: 0.9,
    },
    curvature: 0.2,
    // Edge label background
    labelBg: {
        default: 'rgba(123, 31, 162, 0.08)',
        highlighted: 'rgba(0, 191, 165, 0.15)',
    },
    labelBorder: {
        default: 'rgba(123, 31, 162, 0.3)',
        highlighted: HIGHLIGHT_COLORS.edge,
    },
};

/** Theme-aware edge styles */
export const getEdgeStyles = (isDarkMode: boolean) => {
    const highlights = getHighlightColors(isDarkMode);
    return {
        ...EDGE_STYLES,
        colors: {
            ...EDGE_STYLES.colors,
            highlighted: highlights.edge,
        },
        labelBg: {
            default: isDarkMode ? 'rgba(180, 120, 220, 0.12)' : 'rgba(123, 31, 162, 0.08)',
            highlighted: isDarkMode ? 'rgba(29, 233, 182, 0.15)' : 'rgba(0, 191, 165, 0.15)',
        },
        labelBorder: {
            default: isDarkMode ? 'rgba(180, 120, 220, 0.4)' : 'rgba(123, 31, 162, 0.3)',
            highlighted: highlights.edge,
        },
    };
};

// ============================================================
// GLOW EFFECTS — For selected/connected nodes
// ============================================================
export const GLOW_EFFECTS = {
    selected: {
        color: HIGHLIGHT_COLORS.selected,
        blur: 25,
    },
    connected: {
        color: HIGHLIGHT_COLORS.connected,
        blur: 18,
    },
};

/** Theme-aware glow effects */
export const getGlowEffects = (isDarkMode: boolean) => {
    const highlights = getHighlightColors(isDarkMode);
    return {
        selected: {
            color: highlights.selected,
            blur: 25,
        },
        connected: {
            color: highlights.connected,
            blur: isDarkMode ? 22 : 18, // Slightly stronger glow in dark mode
        },
    };
};

// ============================================================
// FORCE LAYOUT CONFIGURATION
// ============================================================
export const FORCE_CONFIG = {
    chargeStrength: -300,  // Slightly less repulsion to keep nodes closer
    linkDistance: 120,
    alphaDecay: 0.022,     // Faster settling (d3 default is ~0.0228)
    velocityDecay: 0.3,    // Higher friction to prevent excessive drifting (d3 default is 0.4)
    gravity: 0.01,         // Custom centering force strength
};

// ============================================================
// ZOOM CONFIGURATION
// ============================================================
export const ZOOM_CONFIG = {
    min: 0.2,
    max: 15,
    zoomToFitPadding: 50,
    zoomToFitDuration: 400,
    zoomStep: 1.5,         // Multiplier for zoom in/out buttons
};

// ============================================================
// CONTROL BUTTON STYLING — Uses design system values
// ============================================================
export const CONTROL_BUTTON_STYLES = {
    backgroundColor: tokens.glass.whiteStrong,
    hoverBackgroundColor: tokens.colors.surfaceMuted,
    borderRadius: tokens.radiusPx.sm,
    shadow: tokens.shadows.md,
};

/** Theme-aware control styles */
export const getControlStyles = (isDarkMode: boolean) => ({
    ...CONTROL_BUTTON_STYLES,
    backgroundColor: isDarkMode ? tokens.darkColors.surface : tokens.glass.whiteStrong,
    hoverBackgroundColor: isDarkMode ? tokens.darkColors.hover : tokens.colors.surfaceMuted,
});
